import React from 'react';
import {Keyboard, Platform, Pressable, StyleSheet, View} from 'react-native';
import {faEnvelope} from '@fortawesome/free-regular-svg-icons/faEnvelope';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {useFormik} from 'formik';
import * as Yup from 'yup';

import Button from '../../components/ui-controls/Button';
import TextInput from '../../components/ui-controls/form/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {
  AccountScreenAction,
  RouteNavigatorScreenProps,
} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventScreenName} from '../../utils/analytics/events';

const EmailSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email address is required'),
});

const AccountEntryScreen = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_NOT_LOGGED_IN>) => {
  const {userStore} = useStores();

  const [isSubmitting, setSubmitting] = React.useState(false);

  const {handleChange, handleBlur, handleSubmit, errors, touched, values} =
    useFormik({
      validationSchema: EmailSchema,
      initialValues: {email: ''},
      onSubmit: async formValues => {
        setSubmitting(true);
        let data: any;
        if (!config.FF_LOYALTY_ENABLED) {
          data = await userStore.checkEmail(formValues.email);
        } else {
          data = await userStore.checkEmailNew(formValues.email);
        }

        if (data?.canSignIn) {
          navigation.navigate(AppRoute.ACCOUNT_LOGIN, {
            email: values.email,
          });
        } else if (data?.canSignUp) {
          navigation.navigate(AppRoute.ACCOUNT_CREATE, {
            email: values.email,
            action: AccountScreenAction.CREATE,
          });
        } else if (data?.existsInZen) {
          navigation.navigate(AppRoute.ROOT_REWARDS_FLOW, {
            screen: AppRoute.REWARDS_FULL_SIGN_UP,
            params: {
              firstName: data.firstName,
            },
          });
        }

        setSubmitting(false);
      },
    });

  return (
    <ContentView style={styles.main}>
      <View style={styles.inputForm}>
        <TextInput
          value={values.email}
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect={false}
          autoFocus={true}
          errorMessageWhenTouched={errors.email}
          keyboardType="email-address"
          label="Email Address"
          leftIcon={<FontAwesomeIcon icon={faEnvelope} />}
          onBlur={handleBlur('email')}
          onChangeText={handleChange('email')}
          onSubmitEditing={() => handleSubmit()}
          placeholder="Enter your email"
          returnKeyLabel="next"
          returnKeyType="next"
          touched={touched.email}
        />
      </View>
      <Button
        onPress={() => handleSubmit()}
        title="Next"
        disabled={isSubmitting}
        loading={isSubmitting}
      />
      {values.email && !errors.email ? (
        <Pressable
          style={styles.resetOuter}
          onPress={() => {
            if (Platform.OS === 'android') Keyboard.dismiss();
            userStore.initiatePasswordReset(values.email, () =>
              navigation.navigate(AppRoute.HOME),
            );

            Analytics.logEvent({
              eventName: EventName.PasswordResetRequest,
              parameters: {
                screen_name: EventScreenName.AccountEntry,
              },
            });
          }}>
          <Text style={styles.resetLink}>Request password reset</Text>
        </Pressable>
      ) : (
        <View style={styles.resetOuter}>
          <Text style={styles.resetText}>
            Need a password reset? Enter your account email.
          </Text>
        </View>
      )}
    </ContentView>
  );
};

export default AccountEntryScreen;

const styles = StyleSheet.create({
  main: {
    flex: 1,
    alignItems: 'stretch',
    ...Platform.select({
      native: {
        justifyContent: 'flex-start',
      },
      web: {
        justifyContent: 'center',
      },
    }),
  },
  inputForm: {
    marginBottom: 16,
  },
  validationError: {
    fontSize: 16,
  },
  resetOuter: {
    alignItems: 'center',
    marginTop: 16,
  },
  resetLink: {
    color: theme.colour.text.heading.primary,
    fontSize: 14,
  },
  resetText: {
    color: theme.colour.text.content.primary,
    fontSize: 12,
  },
});
